import React from 'react';
import { Tooltip } from '@mui/material';
import StarIcon from '@mui/icons-material/StarRounded';
import StarBorderIcon from '@mui/icons-material/StarOutlineRounded';
import cx from 'classnames';
import { useClipFavourites } from './context';
import styles from './Favourites.module.scss';

type FavouriteStarProps = {
  clipId?: string;
  badge?: boolean;
  clipWidth?: number;
  angleLocked?: boolean;
  toolbar?: boolean;
};

export const FavouriteStar = ({ clipId, badge = false, clipWidth, angleLocked = false, toolbar = false }: FavouriteStarProps) => {
  const { ids, pending, loading, error, toggle, editor } = useClipFavourites();
  const targetId = toolbar ? editor.activeClipId : clipId;
  const favourite = !!targetId && ids.has(String(targetId));
  const label = favourite ? 'Remove from favourites (−)' : 'Add to favourites (+)';
  if (badge) {
    const compact = clipWidth !== undefined && clipWidth < 18;
    const pairedSize = angleLocked && clipWidth !== undefined && clipWidth < 30 ? Math.max(0, Math.min(8, (clipWidth - 3) / 2)) : undefined;
    return <>
      {favourite && <span className={cx(styles.badge, compact && styles.badgeCompact)}
        style={pairedSize !== undefined ? { left: 1, width: pairedSize, height: pairedSize, transform: 'translateY(-50%)' } : undefined}
        role="img" aria-label="Favourite clip">
        <StarIcon />
      </span>}
      <FavouriteFeedback clipId={clipId} />
    </>;
  }
  return (
    <Tooltip title={label}>
      <span className={toolbar ? styles.toolbarWrapper : undefined}>
        <button
          type="button"
          data-preserve-clip-workspace={toolbar ? true : undefined}
          className={toolbar ? styles.toolbarStar : styles.star}
          aria-label={label}
          aria-pressed={favourite}
          disabled={!targetId || loading || error || pending.has(String(targetId))}
          onMouseDown={(event) => { event.preventDefault(); event.stopPropagation(); }}
          onClick={(event) => {
            event.stopPropagation();
            if (targetId) toggle(String(targetId));
          }}
        >
          {favourite ? <StarIcon /> : <StarBorderIcon />}
          {toolbar && <span className={styles.toolbarLabel}>{favourite ? 'Unfavourite' : 'Favourite'}</span>}
        </button>
      </span>
    </Tooltip>
  );
};

export const FavouriteFeedback = ({ clipId, video = false }: { clipId?: string; video?: boolean }) => {
  const { feedback } = useClipFavourites();
  if (!feedback || (!video && (!clipId || !feedback.clipIds.has(String(clipId))))) return null;
  return (
    <React.Fragment key={feedback.key}>
      {video && <span className={styles.videoScreenFlash} aria-hidden="true" />}
      <span className={cx(styles.starFeedback, video && styles.videoFeedback)}
        role={video ? 'status' : undefined} aria-label={video ? (feedback.favourite ? 'Added to favourites' : 'Removed from favourites') : undefined}
        aria-hidden={video ? undefined : true}>
        {feedback.favourite ? <StarIcon /> : <StarBorderIcon />}
        <span className={styles.feedbackSign}>{feedback.favourite ? '+' : '−'}</span>
      </span>
    </React.Fragment>
  );
};
